import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSetAtom, useAtom } from 'jotai';
import { dashSummaryAtom } from '../atoms-types/dashSummary';
import DashSummary from '../atoms-types/dashSummary';
import useWebSocket from '../wsHook';
import { convertWindDirection } from '../converters/windDirectionConverter';
import WindDirGauge from '../charts-graphs/windDirGauge';
import TempChart from '../charts-graphs/tempHumidSumGraph';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { endpointLink, websocketLink } from '../../index';

const DashSummaryHandler: React.FC= () => {
  const { messages } = useWebSocket(websocketLink, ['power', 'sensor']);
  const [dashSumData, setDashSumData] = useAtom(dashSummaryAtom);
  const isSmallScreen = useMediaQuery('(max-width:900px)');

  const { isFetching, isError, error } = useQuery({
    queryKey: ['dashSummary'],
    staleTime: Infinity,
    queryFn: async () => {
      const response = await axios.get<DashSummary>(`${endpointLink}dashsummary`);
      const data = response.data;
      UpdateDashSumData(data);
      return data;
    }
  });

  function UpdateDashSumData(data: DashSummary) {
    if (!data) {
      console.error("No data available to update dash summary information");
      return;
    }

    setDashSumData((prev) => {
      const windDirInfo = data.environSum.WindDir ? convertWindDirection(Number(data.environSum.WindDir)) : null;
      const updatedData = {
        environSum: {
          minTempF: Number(data.environSum.minTempF?.toFixed(1)) || 0,
          maxTempF: Number(data.environSum.maxTempF?.toFixed(1)) || 0,
          TempF: Number(data.environSum.TempF?.toFixed(1)) || 0,
          minHumidity: Number(data.environSum.minHumidity?.toFixed(1)) || 0,
          maxHumidity: Number(data.environSum.maxHumidity?.toFixed(1)) || 0,
          Humidity: Number(data.environSum.Humidity?.toFixed(1)) || 0,
          WindMax: Number(data.environSum.WindMax?.toFixed(3)) || 0,
          WindAvg: Number(data.environSum.WindAvg?.toFixed(3)) || 0,
          WindDir: windDirInfo ? windDirInfo.normalizedDegrees : 0,
          WindDirStr: windDirInfo ? windDirInfo.direction : ""
        },
        powerSum: {
          totalSolarKwh: Number(data.powerSum.totalSolarKwh?.toFixed(3)) || 0,
          totalLoadKwh: Number(data.powerSum.totalLoadKwh?.toFixed(3)) || 0,
          totalImportedKwh: Number(data.powerSum.totalImportedKwh?.toFixed(3)) || 0,
          totalExportedKwh: Number(data.powerSum.totalExportedKwh?.toFixed(3)) || 0,
          DayPeak: Number(data.powerSum.DayPeak?.toFixed(3)) || 0,
          PeakTime: data.powerSum.PeakTime || ""
        }
      };
      return updatedData;
    });
  }

  useEffect(() => {
    messages.forEach((messageObj) => {
      const { channel, message } = messageObj;

      if (channel === 'power') {
        try {
          if (message && typeof message === "object" && "BuildingID" in message) {
            if (message.BuildingID === "pool") {
              const powerMessage = message as { BuildingID: string; DayPeak?: number; PeakTime?: number };
              
              setDashSumData((prev: DashSummary) => ({
                ...prev,
                powerSum: {
                  ...prev.powerSum,
                  DayPeak: Number(powerMessage.DayPeak?.toFixed(3)) || prev.powerSum.DayPeak,
                  PeakTime: powerMessage.PeakTime ? String(powerMessage.PeakTime) : prev.powerSum.PeakTime
                }
              }));
            }
            // Silently ignore messages for other buildings
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      } else if (channel === 'sensor') {
        try {
          if (message && typeof message === "object" && "deviceEUI" in message && message.deviceEUI === "2CF7F1C06090023A") {
            const sensorData = message as {
              WindDir?: number;
              TempF?: number;
              Humidity?: number;
            };
            
            setDashSumData((prev: DashSummary) => {
              const windDirInfo = sensorData.WindDir ? convertWindDirection(sensorData.WindDir) : null;
              return {
                ...prev,
                environSum: {
                  ...prev.environSum,
                  TempF: Number(sensorData.TempF?.toFixed(1)) || prev.environSum.TempF,
                  Humidity: Number(sensorData.Humidity?.toFixed(1)) || prev.environSum.Humidity,
                  WindDir: windDirInfo ? windDirInfo.normalizedDegrees : prev.environSum.WindDir,
                  WindDirStr: windDirInfo ? windDirInfo.direction : prev.environSum.WindDirStr
                }
              };
            });
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      }
    });
  }, [messages, setDashSumData]);

  const renderLoadingOrError = () => {
    if (isFetching) {
      return <p>Loading...</p>;
    } else if (isError) {
      return <p>Error: {error.message}</p>;
    }
    return null;
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {renderLoadingOrError()}
      {isSmallScreen ? (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          maxWidth: '400px',
          p: 2
        }}>
          <Box sx={{ 
            bgcolor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            boxShadow: 1
          }}>
            <Typography variant="h6" gutterBottom color="error.main">Temperature</Typography>
            <Typography variant="body1">Current: {dashSumData.environSum.TempF}°F</Typography>
            <Typography variant="body1">Min: {dashSumData.environSum.minTempF}°F</Typography>
            <Typography variant="body1">Max: {dashSumData.environSum.maxTempF}°F</Typography>
          </Box>
          <Box sx={{ 
            bgcolor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            boxShadow: 1
          }}>
            <Typography variant="h6" gutterBottom color="warning.main">Humidity</Typography>
            <Typography variant="body1">Current: {dashSumData.environSum.Humidity}%</Typography>
            <Typography variant="body1">Min: {dashSumData.environSum.minHumidity}%</Typography>
            <Typography variant="body1">Max: {dashSumData.environSum.maxHumidity}%</Typography>
          </Box>
          <Box sx={{ 
            bgcolor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            boxShadow: 1
          }}>
            <Typography variant="h6" gutterBottom color="info.main">Wind Direction</Typography>
            <Typography variant="body1">Direction: {dashSumData.environSum.WindDirStr}</Typography>
            <Typography variant="body1">Degrees: {dashSumData.environSum.WindDir}°</Typography>
          </Box>
          <Box sx={{ 
            bgcolor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            boxShadow: 1
          }}>
            <Typography variant="h6" gutterBottom color="info.main">Wind Speed</Typography>
            <Typography variant="body1">Max: {dashSumData.environSum.WindMax} mph</Typography>
            <Typography variant="body1">Avg: {dashSumData.environSum.WindAvg} mph</Typography>
          </Box>
          <Box sx={{ 
            bgcolor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            boxShadow: 1
          }}>
            <Typography variant="h6" gutterBottom color="success.main">Precipitation</Typography>
            <Typography variant="body1">Last 24 Hrs: 0.25 inches</Typography>
            <Typography variant="body1">Last 7 Days: 1.5 inches</Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          width: '100%',
          maxWidth: '1200px'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: 2,
            flex: 2
          }}>
            <Box sx={{ flex: 1 }}>
              <TempChart
                minValue={dashSumData.environSum.minTempF}
                maxValue={dashSumData.environSum.maxTempF}
                type="Temp"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TempChart
                minValue={dashSumData.environSum.minHumidity}
                maxValue={dashSumData.environSum.maxHumidity}
                type="Humidity"
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <WindDirGauge />
          </Box>
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            p: 2
          }}>
            <Box sx={{ 
              bgcolor: 'background.paper', 
              p: 2, 
              borderRadius: 1,
              boxShadow: 1
            }}>
              <Typography variant="h6" gutterBottom color="info.main">Wind Speed</Typography>
              <Typography variant="body1">Max: {dashSumData.environSum.WindMax} mph</Typography>
              <Typography variant="body1">Avg: {dashSumData.environSum.WindAvg} mph</Typography>
            </Box>
            <Box sx={{ 
              bgcolor: 'background.paper', 
              p: 2, 
              borderRadius: 1,
              boxShadow: 1
            }}>
              <Typography variant="h6" gutterBottom color="success.main">Precipitation</Typography>
              <Typography variant="body1">Last 24 Hrs: 0.25 inches</Typography>
              <Typography variant="body1">Last 7 Days: 1.5 inches</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DashSummaryHandler;